/**
 * ListenToAnswer — "Listen ›" button for a Q&A answer.
 *
 * Mirrors ListenToArticle. Probes cache on mount; on click, hits
 * /questions/{id}/tts/generate to synthesize + cache, then swaps to
 * a native audio player.
 */
import { useCallback, useEffect, useState } from "react";
import { Headphones, Loader2, AlertCircle } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

export default function ListenToAnswer({ qid }) {
  const [state, setState] = useState({ status: "unknown", meta: null, error: null });

  useEffect(() => {
    if (!qid) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/questions/${qid}/tts/status`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!alive) return;
        setState(d.ready
          ? { status: "ready", meta: d, error: null }
          : { status: "idle", meta: null, error: null });
      } catch {
        if (alive) setState({ status: "idle", meta: null, error: null });
      }
    })();
    return () => { alive = false; };
  }, [qid]);

  const generate = useCallback(async () => {
    setState((s) => ({ ...s, status: "loading", error: null }));
    try {
      const r = await fetch(`${BASE}/questions/${qid}/tts/generate`, { method: "POST" });
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      const d = await r.json();
      setState({ status: "ready", meta: d, error: null });
    } catch (e) {
      setState({ status: "error", meta: null, error: e.message || "Couldn't generate audio." });
    }
  }, [qid]);

  if (state.status === "ready" && state.meta) {
    const durMin = state.meta.duration_s
      ? Math.max(1, Math.round(state.meta.duration_s / 60))
      : null;
    return (
      <div className="mt-3 rounded-xl border bg-slate-50 px-3 py-2" style={{ borderColor: "rgba(15,118,110,0.18)" }} data-testid={`qa-tts-player-${qid}`}>
        <div className="flex items-center gap-2 mb-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
          <Headphones className="w-3 h-3" />
          Listen to this answer
          {durMin && <span className="text-slate-400 normal-case tracking-normal">· {durMin} min</span>}
        </div>
        <audio
          controls
          preload="none"
          src={`${process.env.REACT_APP_BACKEND_URL || ""}${state.meta.mp3_url}`}
          className="w-full h-8"
          data-testid={`qa-tts-audio-${qid}`}
        />
      </div>
    );
  }

  if (state.status === "loading") {
    return (
      <button
        type="button"
        disabled
        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium"
        data-testid={`qa-tts-loading-${qid}`}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating audio…
      </button>
    );
  }

  if (state.status === "error") {
    return (
      <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 text-xs border border-amber-200" data-testid={`qa-tts-error-${qid}`}>
        <AlertCircle className="w-3.5 h-3.5" />
        {state.error}
        <button
          type="button"
          onClick={generate}
          className="ml-1 underline hover:no-underline"
          data-testid={`qa-tts-retry-${qid}`}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={generate}
      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-semibold hover:opacity-90 transition-opacity"
      style={{ background: "#0F766E" }}
      data-testid={`qa-tts-listen-${qid}`}
    >
      <Headphones className="w-3.5 h-3.5" />
      Listen to this answer ›
    </button>
  );
}
